<template>
  <div class="time-line-view">
    <div class="trigger-wrapper">
      <ZoomCenterTransition>
        <TriggerButton
          v-if="!showPanel"
          @click="showPanel = true"
          icon="timeline"
        />
      </ZoomCenterTransition>
    </div>
    <slide-y-down-transition>
      <div v-if="showPanel" class="time-line">
        <div class="time-widget">
          <div class="time-widget-header">
            <spinner v-if="isRequestInProgress" small />
            <icon v-else size="25">timeline</icon>
            <h4>Time Line</h4>
            <expand />
            <DecoupledDateFrequencyPicker
              :getSelectedTime="getSelectedTime"
              :getActiveTimeForDisplayViaProps="getActiveTimeForDisplayViaProps"
              :getActiveTimeWindow="getActiveTimeWindow"
              @saveTimeFilters="saveTimeFilters"
            />
            <icon size="18" type="button" @click="showPanel = false"
              >clear</icon
            >
          </div>
          <Chart
            :constructor-type="'stockChart'"
            :options="getChartOptions"
            id="time-widget"
            ref="timeWidget"
          ></Chart>
        </div>
      </div>
    </slide-y-down-transition>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import moment from "@/lib/moment";
import HighchartsVue, { Chart } from "highcharts-vue";
import stockInit from "highcharts/modules/stock";
import Highcharts from "highcharts";
Vue.use(HighchartsVue);
stockInit(Highcharts);
import { format } from "@/lib/numberFormatter";
import Button from "@/components/metrics/views/Button";
import DecoupledDateFrequencyPicker from "@/components/datetimepicker/DecoupledDateFrequencyPicker";
import { ZoomCenterTransition, SlideYDownTransition } from "vue2-transitions";
import TriggerButton from "@/components/static_entity_analysis/views/TriggerButton";

export default {
  components: {
    Chart,
    Button,
    DecoupledDateFrequencyPicker,
    ZoomCenterTransition,
    SlideYDownTransition,
    TriggerButton,
  },
  data() {
    return {
      chartData: [],
      liveInterval: null,
      playerInterval: null,
      isPlaying: false,
      showPanel: true,
      chartOptions: {
        credits: {
          enabled: false,
        },
        chart: {
          height: "170px",
          backgroundColor: {
            solid: "var(--font-color-unselected)",
          },
        },
        rangeSelector: {
          inputEnabled: false,
          enabled: false,
          inputDateFormat: "%Y-%d-%m-%H-%M",
          inputEditDateFormat: "%Y-%d-%m-%H-%M",
        },
        scrollbar: {
          liveRedraw: false,
        },
        xAxis: {
          minRange: 360,
          type: "datetime",
          labels: {
            format: "{value:%d-%m-%Y}",
          },
          events: {
            afterSetExtremes: this.addTimeFilter,
          },
          gridLineColor: "var(--border-color)",
          labels: {
            style: {
              color: "var(--font-color)",
            },
          },
          lineColor: "var(--font-color)",
          minorGridLineColor: "var(--font-color-unselected)",
          tickColor: "var(--border-color)",
          title: {
            style: {
              color: "var(--font-color-unselected)",
            },
          },
        },
        yAxis: [],
        series: [],
        navigator: {
          enabled: false,
          height: 25,
          handles: {
            backgroundColor: "var(--color-primary)",
            borderColor: "var(--color-secondary)",
            height: 20,
          },
          outlineColor: "var(--border-color)",
          maskFill: "var(--color-primary-light)",
          series: {
            color: "var(--color-accent)",
            lineColor: "var(--color-accent)",
          },
          xAxis: {
            gridLineColor: "var(--font-color-unselected)",
          },
        },
        scrollbar: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters("SupplyDemand", ["getTimeDataById"]),
    ...mapGetters("Layers", ["getActiveLayers", "getActiveLayerDetails"]),
    ...mapGetters("Data", ["isRequestInProgress"]),
    ...mapGetters("Time", ["getActiveTimeWindow", "getActiveTimeSlice"]),
    getSelectedTime() {
      // Used for DecoupledDateFrequencyPicker
      if (this.getActiveTimeSlice && this.getActiveTimeSlice.t_start) {
        const { t_start, t_end } = this.getActiveTimeSlice;
        let startTime = moment(t_start * 1000).format("DD/MM/YY h:mm a");
        let endTime = moment(t_end * 1000).format("DD/MM/YY h:mm a");
        return `${startTime} to ${endTime}`;
      } else {
        return null;
      }
    },
    getActiveTimeForDisplayViaProps() {
      // Used for DecoupledDateFrequencyPicker
      if (this.getActiveTimeWindow && this.getActiveTimeWindow.length > 1) {
        const t_start = this.getActiveTimeWindow[0];
        const t_end = this.getActiveTimeWindow[1];
        let startTime = moment(t_start).format("DD/MM/YY h:mm a");
        let endTime = moment(t_end).format("DD/MM/YY h:mm a");
        return `${startTime} to ${endTime}`;
      } else {
        return null;
      }
    },
    getChartOptions() {
      // If there are no Active Layers, just return chartOptions with no series:
      if (
        !(
          this.getActiveLayers &&
          Object.entries(this.getActiveLayers).length == 2
        )
      )
        return this.chartOptions;

      // Get the layer Details:
      let {
        SUPPLY: supplyLayer,
        DEMAND: demandLayer,
      } = this.getActiveLayerDetails;

      // Get the Time Data:
      let supplyLayerTimeData = this.getTimeDataById(supplyLayer.id);
      let demandLayerTimeData = this.getTimeDataById(demandLayer.id);

      // If time data is not present for the selected layers, then just return chartOptions with no series:
      if (
        !(
          supplyLayerTimeData &&
          supplyLayerTimeData.length > 0 &&
          demandLayerTimeData &&
          demandLayerTimeData.length > 0
        )
      )
        return this.chartOptions;

      let supplySeries = supplyLayerTimeData.map(timeData => {
        return {
          type: "line",
          color: "var(--color-primary)",
          name: timeData.meta.name,
          yAxis: 0,
          // Hack for new data structure of analysis time response, added [0]
          data:
            // TODO: Make this modification at the time of saving:
            timeData.data[0] && timeData.data[0].length > 0
              ? timeData.data[0].map(item => {
                  return [item[0] * 1000, item[1]];
                })
              : [],
        };
      });

      let supplyYAxis = {
        lineColor: "var(--color-primary)",
        opposite: false,
        gridLineColor: "var(--font-color-unselected)",
        title: {
          text: supplyLayer.name,
          style: {
            color: "var(--color-primary)",
          },
        },
        labels: {
          style: {
            color: "var(--color-primary)",
          },
        },
      };

      let demandYAxis = {
        lineColor: "var(--color-primary)",
        opposite: true,
        gridLineColor: "var(--font-color-unselected)",
        title: {
          text: demandLayer.name,
          style: {
            color: "var(--color-accent)",
          },
        },
        labels: {
          style: {
            color: "var(--color-accent)",
          },
        },
      };

      let demandSeries = demandLayerTimeData.map(timeData => {
        return {
          type: "line",
          color: "var(--color-accent)",
          name: timeData.meta.name,
          yAxis: 1,
          // Hack for new data structure of analysis time response, added [0]
          data:
            // TODO: Make this modification at the time of saving:
            timeData.data[0] && timeData.data[0].length > 0
              ? timeData.data[0].map(item => {
                  return [item[0] * 1000, item[1]];
                })
              : [],
        };
      });

      return {
        ...this.chartOptions,
        yAxis: [supplyYAxis, demandYAxis],
        series: [supplySeries[0], demandSeries[0]],
      };
    },
  },
  methods: {
    // ...mapActions("TimeManager", [
    //   "setActiveTimeSlice",
    //   "setActiveTimeWindow",
    //   "setActiveDays",
    //   "setActivePeaks",
    // ]),
    ...mapActions("Time", [
      "setActiveTimeSlice",
      "setActiveTimeWindow",
      "setActiveDays",
      "setActivePeaks",
    ]),
    ...mapActions("SupplyDemand", ["refetchAllWidgets"]),
    addTimeFilter(event) {
      // do not refresh the console if it is not user set event
      if (event.type === "afterSetExtremes") return;
      if (event) {
        const newTime = {
          t_start: Math.floor(event.min / 1000),
          t_end: Math.floor(event.max / 1000),
        };

        this.setActiveTimeSlice(newTime).then(() => {
          // this.$emit("change");
        });
      }
    },
    saveTimeFilters(timeFilters) {
      // DO THIS INTELLIGENTLY::
      this.setActiveTimeWindow(timeFilters.selectedDate);
      this.setActiveDays(timeFilters.selectedDays);
      this.setActivePeaks(timeFilters.selectedPeaks);
      // this.$emit("changedTimeFilter");
      // Toggle Change time
      this.refetchAllWidgets();
    },
  },
};
</script>

<style lang="scss" scoped>
.time-line-view {
  position: absolute;
  bottom: 12px;
  margin: 0 auto;
  left: 0;
  right: 0;

  .trigger-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    bottom: 0;
  }
}
.time-line {
  position: absolute;
  bottom: 12px;
  height: 230px;
  background-color: var(--color-secondary-transparent);
  width: 95%;
  max-width: 1000px;
  border: 1px solid var(--border-color);
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr;
  transition: 0.3s all ease-in-out;

  &.hide {
    transform: translateY(300px);
  }

  @include respond-below(lg) {
    width: 100%;
    bottom: 0;
    border-radius: 0;
  }

  .time-widget {
    display: flex;
    flex-direction: column;
    min-width: 0;

    #time-widget {
      width: auto;
    }

    .time-widget-header {
      background: var(--color-secondary);
      padding: 10px 10px 10px 15px;
      display: flex;
      flex-direction: row;
      align-items: center;
      border-bottom: 1px solid var(--border-color);
      border-radius: 5px;

      .player-controls {
        display: flex;
        align-items: center;
        margin: 0px 10px;

        i {
          background: var(--color-primary-light);
          padding: 7px 10px;
          margin-left: 0px;
          transition: 0.3s all ease-in-out;

          &:first-child {
            border-radius: 5px 0 0 5px;
            background: var(--color-primary);
            color: var(--color-secondary);
          }

          &:last-child {
            border-radius: 0 5px 5px 0;
            background: var(--font-color-unselected);
          }

          &:hover {
            opacity: 0.8;
            cursor: pointer;
          }
        }
      }

      h4 {
        margin-left: 10px;
      }

      .time-selection-row {
        display: flex;
        flex-direction: row;
        align-items: center;

        button {
          width: 30px;
          height: 30px;
          padding: 0;
          margin: 0 5px;
          border: 0;
          border-radius: 50%;
          background-color: var(--font-color-unselected);
          color: var(--font-color);
          font-size: 0.8em;
          font-weight: bold;
          transition: 0.2s all ease-in-out;
          outline: none;

          &:hover {
            cursor: pointer;
            background-color: var(--color-primary-light);
          }

          &:active {
            transform: scale(0.9);
          }

          &.active {
            background-color: var(--color-primary);
            color: var(--color-secondary);
          }
        }
      }
    }
  }
}
</style>
